import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import compose from 'lodash/fp/compose';
import { useBlogPostDetail } from '../../hooks/useBlog';
import { withTranslation, withAccount } from '../hoc';
// import CommentSection from './CommentSection'; // Uncomment when needed
import { Icon } from 'semantic-ui-react';
import { 
  ArrowLeft, 
  Clock, 
  Eye, 
  Calendar, 
  Person, 
  Star, 
  Tag,
  Share,
  BookmarkPlus,
  Twitter,
  Linkedin,
  Facebook
} from 'react-bootstrap-icons';

const BRAND_COLOR = '#739bcb';
const BRAND_COLOR_DARK = '#2d5a90'; // Darker shade for gradients/active states
const TEXT_MAIN = '#739bcb'; // Very dark grey for titles
const TEXT_BODY = '#334155'; // Dark grey for clear reading
const BG_COLOR = '#f8fafc';

// --- Animations ---
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// --- Styled Components ---

const BlogDetailWrapper = styled.div`
  min-height: 100vh;
  background-color: ${BG_COLOR};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  color: ${TEXT_MAIN};
`;

const HeroSection = styled.div`
  position: relative;
  background: linear-gradient(135deg, ${BRAND_COLOR} 0%, ${BRAND_COLOR_DARK} 100%);
  padding: 3rem 2rem 10rem 2rem; /* Extra bottom padding for overlap */
  color: #fff;
  overflow: hidden;

  /* Abstract Background Shapes */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -20%;
    width: 80%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 60%);
    transform: rotate(15deg);
  }
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  position: relative;
  z-index: 2;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 2rem;
  transition: all 0.2s ease;

  &:hover {
    color: #fff;
    transform: translateX(-4px);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ContentWrapper = styled.div`
  margin-top: -7rem; /* Pulls card up over hero */
  animation: ${fadeIn} 0.6s ease-out;
  
  @media (max-width: 768px) {
    margin-top: -4rem;
  }
`;

const ArticleCard = styled.article`
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0,0,0,0.05);
  overflow: hidden;
  margin-bottom: 3rem;
`;

const FeaturedImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 500px;
  background: ${props => props.image 
    ? `url(${props.image})` 
    : `linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)`};
  background-size: cover;
  background-position: center;

  /* Subtle overlay for text contrast if needed, though text is below now */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: linear-gradient(to bottom, transparent, rgba(0, 0, 0, 0.05));
  }

  @media (max-width: 768px) {
    height: 300px;
  }
`;

const FeaturedBadge = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  color: #d97706;
  padding: 0.6rem 1.2rem;
  border-radius: 100px;
  font-size: 0.85rem;
  font-weight: 700;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ArticleContent = styled.div`
  padding: 4rem;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

const CategoryBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${BRAND_COLOR};
  font-size: 0.9rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;
`;

const ArticleTitle = styled.h1`
  font-size: 2rem;
  color: ${TEXT_MAIN};
  margin: 0 0 1.5rem 0;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: -0.02em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const MetaSection = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  align-items: center;
  padding: 1.5rem 0;
  border-top: 1px solid #e2e8f0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 3rem;
`;

const AuthorMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e2e8f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${BRAND_COLOR};
  }

  div {
    display: flex;
    flex-direction: column;
    
    strong { color: ${TEXT_MAIN}; font-weight: 600; font-size: 0.95rem; }
    span { color: #64748b; font-size: 0.85rem; }
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 500;

  svg {
    color: #94a3b8;
  }
`;

const TagsSection = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 2.5rem;
`;

const TagItem = styled.span`
  background: #f1f5f9;
  color: ${TEXT_BODY};
  padding: 0.4rem 1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.2s ease;
  border: 1px solid transparent;

  &:hover {
    border-color: ${BRAND_COLOR};
    color: ${BRAND_COLOR};
    background: #fff;
  }
`;

const ArticleBody = styled.div`
  line-height: 1.9;
  font-size: 1.125rem;
  color: ${TEXT_BODY};

  /* Typography Standardizing */
  p {
    margin-bottom: 1.75rem;
  }

  /* Fix bold text color */
  strong, b {
    color: ${TEXT_BODY};
    font-weight: 700;
  }

  /* Fix italic text color */
  em, i {
    color: ${TEXT_BODY};
    font-style: italic;
  }

  /* Fix underline text color */
  u {
    color: ${TEXT_BODY};
    text-decoration: underline;
  }

  h2, h3, h4 {
    color: ${TEXT_MAIN};
    font-weight: 800;
    line-height: 1.3;
    margin-top: 3rem;
    margin-bottom: 1.25rem;
  }

  h2 {
    font-size: 2rem;
    letter-spacing: -0.01em;
  }

  h3 {
    font-size: 1.5rem;
  }

  /* Images inside content */
  img {
    max-width: 100%;
    height: auto;
    border-radius: 16px;
    margin: 2rem 0;
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
  }

  /* Quotes */
  blockquote {
    border-left: 4px solid ${BRAND_COLOR};
    margin: 2.5rem 0;
    padding: 1.5rem 2rem;
    background: #f8fafc;
    border-radius: 0 12px 12px 0;
    font-style: italic;
    color: ${TEXT_MAIN};
    font-size: 1.25rem;
    font-weight: 500;
  }

  /* Lists */
  ul, ol {
    margin: 1.5rem 0 2rem 0;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.75rem;
      padding-left: 0.5rem;
      marker: ${BRAND_COLOR};
    }
  }

  ul li::marker {
    color: ${BRAND_COLOR};
  }

  /* Links */
  a {
    color: ${BRAND_COLOR};
    text-decoration: none;
    font-weight: 600;
    border-bottom: 1px solid rgba(45, 90, 144, 0.3);
    transition: all 0.2s ease;

    &:hover {
      border-bottom-color: ${BRAND_COLOR};
      background: rgba(45, 90, 144, 0.05);
    }
  }
  
  /* Code */
  pre {
    background: #1e293b;
    color: #e2e8f0;
    padding: 1.5rem;
    border-radius: 12px;
    overflow-x: auto;
    margin: 2rem 0;
    font-size: 0.9rem;
  }
`;

const ShareSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  padding: 3rem 0 1rem 0;
  border-top: 1px solid #e2e8f0;
  margin-top: 4rem;

  span {
    font-weight: 700;
    color: ${TEXT_MAIN};
    margin-right: 1rem;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

const ShareButton = styled.button`
  background: #fff;
  color: ${TEXT_BODY};
  border: 1px solid #e2e8f0;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.9rem;

  svg {
    width: 16px;
    height: 16px;
    color: #64748b;
    transition: color 0.2s;
  }

  &:hover {
    background: #f8fafc;
    border-color: ${BRAND_COLOR};
    color: ${BRAND_COLOR};
    transform: translateY(-2px);
    
    svg { color: ${BRAND_COLOR}; }
  }
`;

// --- Loading & Error States ---

const LoadingState = styled.div`
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: ${BG_COLOR};

  .spinner {
    width: 50px;
    height: 50px;
    border: 4px solid #e2e8f0;
    border-top-color: ${BRAND_COLOR};
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.5rem;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  p {
    font-weight: 600;
    color: #64748b;
  }
`;

const ErrorState = styled(LoadingState)`
  text-align: center;
  padding: 2rem;

  svg {
    color: #ef4444;
    margin-bottom: 1.5rem;
  }

  h2 {
    font-size: 2rem;
    color: ${TEXT_MAIN};
    margin-bottom: 1rem;
  }

  a {
    margin-top: 2rem;
    background: ${BRAND_COLOR};
    color: #fff;
    padding: 0.75rem 1.5rem;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    transition: background 0.2s;

    &:hover {
      background: ${BRAND_COLOR_DARK};
    }
  }
`;

// --- Component ---

const BlogDetail = (props) => {
  const { tr, account } = props;
  const { t } = useTranslation();
  const { slug } = useParams();
  const { post, isLoading, error } = useBlogPostDetail(slug);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  const isAuth = account?.user?.isAuth;
  const translate = isAuth ? tr : t;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Share Functions
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // const handleTwitterShare = () => {
  //   const text = encodeURIComponent(post.title);
  //   const url = encodeURIComponent(window.location.href);
  //   window.open(
  //     `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
  //     '_blank',
  //     'width=550,height=420'
  //   );
  // };

  const handleFacebookShare = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const handleLinkedInShare = () => {
    const url = encodeURIComponent(window.location.href);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      '_blank',
      'width=550,height=420'
    );
  };

  const handleSavePost = () => {
    // Get saved posts from localStorage
    const savedPosts = JSON.parse(localStorage.getItem('savedBlogPosts') || '[]');
    
    if (saved) {
      // Remove from saved
      const filtered = savedPosts.filter(id => id !== post.id);
      localStorage.setItem('savedBlogPosts', JSON.stringify(filtered));
      setSaved(false);
    } else {
      // Add to saved
      savedPosts.push(post.id);
      localStorage.setItem('savedBlogPosts', JSON.stringify(savedPosts));
      setSaved(true);
    }
  };

  // Check if post is already saved
  useEffect(() => {
    if (post) {
      const savedPosts = JSON.parse(localStorage.getItem('savedBlogPosts') || '[]');
      setSaved(savedPosts.includes(post.id));
    }
  }, [post]);

  if (isLoading) {
    return (
      <LoadingState>
        <div className="spinner" />
        <p>{translate('Loading article...')}</p>
      </LoadingState>
    );
  }

  if (error || !post) {
    return (
      <ErrorState>
        <Icon name="warning sign" size="huge" />
        <h2>{translate('Article not found')}</h2>
        <p>{translate('The article you are looking for does not exist or has been removed.')}</p>
        <Link to="/blog">{translate('Back to Blog')}</Link>
      </ErrorState>
    );
  }

  return (
    <BlogDetailWrapper>
      <HeroSection>
        <Container>
          <BackLink to="/blog">
            <ArrowLeft /> {translate('Back to Articles')}
          </BackLink>
        </Container>
      </HeroSection>

      <Container>
        <ContentWrapper>
          <ArticleCard>
            <FeaturedImageWrapper image={post.featured_image}>
              {post.is_featured && (
                <FeaturedBadge>
                  <Star size={14} /> {translate('Featured')}
                </FeaturedBadge>
              )}
            </FeaturedImageWrapper>

            <ArticleContent>
              {post.category && (
                <CategoryBadge>
                  <Tag size={12} /> {post.category.name}
                </CategoryBadge>
              )}

              <ArticleTitle>{post.title}</ArticleTitle>

              <MetaSection>
                <AuthorMeta>
                  <div className="avatar">
                    <Person size={20} />
                  </div>
                  <div>
                     <strong>{post.author?.username || translate('Anonymous')}</strong>
                     <span>{translate('Author')}</span>
                  </div>
                </AuthorMeta>

                <div style={{ flex: 1 }}></div> {/* Spacer */}

                <MetaItem>
                  <Calendar size={16} />
                  {new Date(post.published_at || post.created_at).toLocaleDateString()}
                </MetaItem>
                <MetaItem>
                  <Clock size={16} />
                  {Math.ceil((post.content?.split(' ').length || 0) / 200)} {translate('min read')}
                </MetaItem>
                <MetaItem>
                  <Eye size={16} />
                  {post.views_count || 0}
                </MetaItem>
              </MetaSection>

              {post.tags && post.tags.length > 0 && (
                <TagsSection>
                  {post.tags.map((tag, idx) => (
                    <TagItem key={idx}>#{tag}</TagItem>
                  ))}
                </TagsSection>
              )}

              <ArticleBody 
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              <ShareSection>
                <span>{translate('Share:')}</span>
                <ShareButton onClick={handleCopyLink}>
                  <Share /> {copied ? translate('Copied!') : translate('Copy Link')}
                </ShareButton>
                {/* <ShareButton onClick={handleTwitterShare}>
                  <Twitter /> Twitter
                </ShareButton> */}
                <ShareButton onClick={handleFacebookShare}>
                  <Facebook /> Facebook
                </ShareButton>
                <ShareButton onClick={handleLinkedInShare}>
                  <Linkedin /> LinkedIn
                </ShareButton>
                <ShareButton 
                  onClick={handleSavePost}
                  style={{ 
                    background: saved ? '#f0f9ff' : '#fff',
                    borderColor: saved ? BRAND_COLOR : '#e2e8f0',
                    color: saved ? BRAND_COLOR : TEXT_BODY
                  }}
                >
                  <BookmarkPlus /> {saved ? translate('Saved') : translate('Save')}
                </ShareButton>
              </ShareSection>
            </ArticleContent>
          </ArticleCard>

          {/* <CommentSection postId={post.id} comments={post.comments || []} /> */}
        </ContentWrapper>
      </Container>
    </BlogDetailWrapper>
  );
};

export default compose(withTranslation, withAccount)(BlogDetail);